import * as React from 'react';
import { getFormattedDateFromIsoStringWithFormat } from '~/src/utils/date';
import { LoadingOverlay } from '~/src/components/PageLayout';
import { LAYOUT_SLIDEIN_TYPES } from '~/src/components/PageLayout/SlideIns';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import useMst from '~/src/hooks/useMst';

import { renderFormField } from '~/src/components/Forms';
import MatterDetail from './MatterDetail';
import { useLayoutContext } from '../../contexts/Layout';

export const MatterDetailsPageContent = ({
  matter,
  clioQuestionnaireSubmissions,
  clioQuestionnaireSubmissionUnmappedQuestions,
  isFetching,
  isError,
  refetch,
}) => {
  const { showSlideIn } = useLayoutContext();
  const { isClioQuestionnaireEnabled } = useFeatureAccesses();

  const { viewContact } = useMst((store) => ({
    viewContact: store.contacts.viewContact,
  }));

  const showContact = (contact) => {
    viewContact(contact.id);
    showSlideIn(LAYOUT_SLIDEIN_TYPES.contact, {
      edit: false,
      id: contact.id,
      matterId: matter?.id,
    });
  };

  if (isFetching)
    return <LoadingOverlay title="Loading matter" subtitle="Please wait" />;

  if (isError) return <div>Error fetching matter.</div>;

  const summaryFields = matter
    .summaryFields()
    .map((field) => renderFormField({ ...field, editable: false }));

  const relatedContactsFields = matter
    .relatedContactsFields()
    .map((field) =>
      renderFormField(
        { ...field, editable: false },
        { onClick: () => showContact(field.contact) },
      ),
    );

  const { documents } = matter.details;
  const { documentSets } = matter.details;

  const dateFormat = matter.details?.dateFormat;
  const entityCardFields = matter.getEntityCardFields();
  const mappedEntityCardFields = {};
  let clioCustomFields = [];
  let areClioFieldsEmpty = true;

  Object.keys(entityCardFields).forEach((key) => {
    const mappedFields = entityCardFields[key].map((field) => {
      // check if field value is date in ISO format and contains a time
      const date = getFormattedDateFromIsoStringWithFormat(
        field.defaultValue,
        dateFormat,
      );
      if (date && field.defaultValue.includes('T')) {
        return renderFormField({
          ...field,
          defaultValue: date,
          editable: false,
        });
      }

      return renderFormField({
        ...field,
        defaultValue: field.defaultValue || '-',
        editable: false,
      });
    });

    if (key === 'clio_matter') {
      clioCustomFields = mappedFields.filter(
        (mappedField) => mappedField.id !== 'number',
      );
      // Check if any clio field has a non-empty value
      areClioFieldsEmpty = entityCardFields[key]
        .filter((field) => field.name !== 'number')
        .every(
          (field) =>
            field.defaultValue === null ||
            field.defaultValue === undefined ||
            field.defaultValue === '',
        );
    } else {
      mappedEntityCardFields[key] = mappedFields;
    }
  });

  return (
    <MatterDetail
      onSynced={refetch}
      summaryFields={summaryFields}
      documents={documents}
      documentSets={documentSets}
      relatedContactsFields={relatedContactsFields}
      clioCustomFields={clioCustomFields}
      areClioFieldsEmpty={areClioFieldsEmpty}
      entityCardFields={mappedEntityCardFields}
      matter={matter}
      isClioQuestionnaireEnabled={isClioQuestionnaireEnabled}
      clioQuestionnaireSubmissions={clioQuestionnaireSubmissions}
      clioQuestionnaireSubmissionUnmappedQuestions={
        clioQuestionnaireSubmissionUnmappedQuestions
      }
    />
  );
};
